<template>
    <div class="dashboard-row dw-10 ath-row br-m">
        <div class="dashboard-header f dw-10 sm-b-l sm-t-l">
            <ButtonIcon icon="plus"
                qa="btn-add-row"
                v-if="sectionCount === 1"
                class="el--s-m el--t-primary sm-r-s"
                :disabled="atRowLimit"
                :title="uiBtnTitle"
                @click.prevent.stop="onAddSection(section)" />
            <DropdownCategorized v-else
                qa="dd-add-row"
                class="sm-r-s"
                :disabled="atRowLimit"
                triggerType="icon"
                btnType="primary"
                :btnTitle="uiBtnTitle"
                :options="sectionTypes"
                @input="onAddSection" />
            <div class="te-fat-light">{{ 'mead.add_section' | athT }}</div>
            <div class="title-divider b-b fg"></div>
        </div>
    </div>
</template>
<script>
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import DropdownCategorized from '../dropdown-categorized/DropdownCategorized.vue';

export default {
    props: {
        /**
         * Identifier of the dashboard type/definition this add-section line belongs
         * to, used to look up its DashboardDefinition (row limits, accessible row
         * types, and per-row-type widget definitions).
         */
        dashboardType: {type: String, default: ''},
        /**
         * Current number of rows already on the dashboard, compared against the
         * dashboard definition's max rows to determine if the row limit has been reached.
         */
        rowCount: {type: Number, default: () => 0},
        /**
         * Remaining widget weight budget available on the dashboard, used to
         * determine which section/widget types can still be added.
         */
        availableWeight: {type: Number, default: () => 0},
        /**
         * Whether additional pages can be added to the dashboard when the row
         * limit is reached, used to pick the appropriate "limit reached" message.
         */
        canAddPages: {type: Boolean, default: false},
    },
    components: {ButtonIcon, DropdownCategorized},
    data: function () {
        return {
            showAddSectionModal: false,
        };
    },
    methods: {
        onAddSection: function (type) {
            this.$emit('onAddRow', type);
        },
        checkIfSectionIsAvailable: function (section_type) {
            let can_add = false;

            for (const el of MeadCommon.DashboardService.getRowDefinition(section_type).getAccessibleChildren()) {
                const widget = MeadCommon.DashboardService.getWidgetDefinition(el);
                if (widget && widget.getWeight() <= this.availableWeight) {
                    can_add = true;

                    break;
                }
            }

            return can_add;
        },
    },
    computed: {
        dashboardDefinition: function () {
            const dashboardDefinition = MeadCommon.DashboardService.getDashboardDefinition(this.dashboardType);
            if (dashboardDefinition !== undefined) return dashboardDefinition;
            return new MeadCommon.DashboardDefinition();
        },
        hasRowLimit: function () {
            return this.dashboardDefinition.hasRowLimit();
        },
        atRowLimit: function () {
            return (this.hasRowLimit && this.rowCount >= this.dashboardDefinition.getMaxRows()) || !this.canAddRows;
        },
        /**
         * Check if the page has at least one section with widgets with weight not bigger then the available weight
         */
        canAddRows: function () {
            let can_add = false;

            for (const type of this.dashboardDefinition.getAccessibleRowTypes()) {
                if (!MeadCommon.DashboardService.getRowDefinition(type).getAccessibleChildren()) continue;

                if (this.checkIfSectionIsAvailable(type)) {
                    can_add = true;
                    break;
                }
            }

            return can_add;
        },
        sectionCount: function () {
            return this.sectionTypes[0].items.length + this.sectionTypes[1].items.length;
        },
        section: function () {
            if (this.sectionCount !== 1) return false;
            const section = this.sectionTypes[0].items[0] || this.sectionTypes[1].items[0];
            return section.value;
        },
        sectionTypes: function () {
            const sections = this.dashboardDefinition.getAccessibleRowTypes();
            return [
                {
                    category: MeadCommon.I18N.go('data_studio.section_types.blank_sections'),
                    items: sections.map(row => ({
                        value: MeadCommon.DashboardService.getRowDefinition(row).type,
                        label: MeadCommon.DashboardService.getRowDefinition(row).label,
                        disabled: !this.checkIfSectionIsAvailable(row),
                    })),
                },
                {
                    category: MeadCommon.I18N.go('data_studio.section_types.preconfigured_sections'),
                    items: [],
                },
            ];
        },
        /**
         * Returns the button title
         */
        uiBtnTitle: function () {
            if (this.canAddRows) return false;

            return this.canAddPages ? 'mead.widget_limit_reached_new_page_available' : 'mead.widget_limit_reached';
        },
    },
};
</script>
<style lang="scss" scoped>
.title-divider {
    height: 2rem;
    margin: 0 0 2rem 2rem;
}

.te-fat-light {
    align-content: center;
}
</style>